import {
  Label,
  Input,
  Toaster,
  useToastController,
  Toast,
  ToastTitle,
  ToastTrigger,
  ToastBody,
  Link,
  Tooltip,
  Button,
} from "@fluentui/react-components";
import MaterialReactTable, {
  MRT_ColumnDef,
  MRT_Cell,
  MRT_PaginationState,
  MRT_SortingState,
  MRT_VisibilityState,
} from "material-react-table";
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import ConfirmationPopup from "./ConfirmationPopup";
import FilterTab from "./FilterTab";
import RecieptForm from "./RecieptForm";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  fetchReceiptList,
  fetchProviders,
  setFilteredData,
  setSelectedProviders,
  postReceipt,
  updateCommission,
} from "../../../redux/Commission/commissionSlice";
import {
  selectTablePagination,
  selectTableSorting,
  selectTableColumnOrder,
  selectTableColumnVisibility,
  selectTableAppliedFilters,
  selectIsDefaultPreference,
  setTablePagination,
  setTableSorting,
  setTableColumnOrder,
  setTableColumnVisibility,
  setTableDraftFilter,
  applyTableFilters,
  saveTableState,
  resetTableStateToDefault,
} from "../../../redux/preferenceSlice";
import defaultTableConfig from "../../../shared/tableConfig.json";
import SavePopup from "./savePopup";
import { debounce, isEqual } from "lodash";
import moment from "moment";
import {
  convertStringToArray,
  formatedDate,
  getClientType,
  getReceiptStatus,
} from "../../../utils";
import ReactQuillComponent from "../../../components/ReactQuillComponent";
import ASCButton from "../../../components/Button";
import { ArrowClockwise20Regular } from "@fluentui/react-icons";
import { useToastMessage } from "../../../components/ToastMessage/ToastMessage";
import { dateOptions } from "../../../shared/commissionDateOptions";
import { TableListLoader } from "../../../components/Loader/TableListLoader";
import { StopReceiptPopUp } from "../../client/case/StopReceiptPopUp";
import HighlightText from "../../../components/HighlightText/index";

const TABLE_NAME = "COMMISSION_RECEIPTS" as const;

const HIGHLIGHT_TRANSFORMS: Record<string, (v: any) => string> = {
  date: formatedDate,
  receiptStatus: getReceiptStatus,
  caseHolder: getClientType,
};

const makeHighlightCell =
  (transformKey: keyof typeof HIGHLIGHT_TRANSFORMS) =>
  ({ row, column }: any) => (
    <HighlightText
      text={HIGHLIGHT_TRANSFORMS[transformKey](row.original[column.id])}
      highlight={String(column.getFilterValue() ?? "")}
    />
  );

const makeHighlightFilterFn =
  (transformKey: keyof typeof HIGHLIGHT_TRANSFORMS) =>
  (row: any, columnId: string, filterValue: any) =>
    HIGHLIGHT_TRANSFORMS[transformKey](row.getValue(columnId))
      .toLowerCase()
      .includes(String(filterValue).trim().toLowerCase());

const ActualInput = React.memo(({ value: initialValue, onSave }: any) => {
  const [localValue, setLocalValue] = useState(initialValue);

  // Sync with external changes (e.g., when the list is refreshed)
  useEffect(() => {
    setLocalValue(initialValue);
  }, [initialValue]);

  const handleBlur = () => {
    if (localValue !== initialValue) {
      onSave(localValue);
    }
  };

  return (
    <Input
      style={{ border: "none" }}
      className="hover-border"
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onBlur={handleBlur} // Only triggers parent re-render when user leaves the field
      type="number"
    />
  );
});

const ReceiptList = () => {
  const { showToast, toasterId } = useToastMessage();
  const [confirmationPopup, setConfirmationPopup] = useState(false);
  const [editpopup, setEditpopup] = useState(false);
  const [stopReceiptpopup, setStopReceiptpopup] = useState(false);
  const [amountReceived, setAmountReceived] = useState("");
  const [receiptedAmount, setReceiptedAmount] = useState(0);
  const [unusedAmount, setUnusedAmount] = useState(0);
  const [selectedRowIds, setSelectedRowIds] = useState<any[]>([]);
  const [receiptEditpopup, setReceiptEditpopup] = useState(false);
  const [filterPopup, setFilterPopup] = useState(false);
  const [confirmsavepopup, setConfirmsavepopup] = useState(false);
  const [activeRowItem, setActiveRowItem] = useState<any>({});
  const [dateOption, setDateOption] = useState<string>("last12Months");
  const dispatch: AppDispatch = useDispatch();
  const { dispatchToast } = useToastController(toasterId);

  const notify = (title?: string, message?: string, type?: any) =>
    dispatchToast(
      <Toast>
        <ToastTitle
          action={
            <ToastTrigger>
              <Link>Dismiss</Link>
            </ToastTrigger>
          }
        >
          {title ?? title}
        </ToastTitle>
        <ToastBody>{message ?? message}</ToastBody>
      </Toast>,
      { intent: type ? type : "success" },
    );

  const receiptList: any[] = useSelector(
    (state: RootState) => state.commission.listItems || [],
  );
  const providers = useSelector(
    (state: RootState) => state.commission.providers,
  );
  const isLoading = useSelector(
    (state: RootState) => state.commission.loading.receiptList,
  );
  const saveLoading = useSelector(
    (state: RootState) => state.commission.loading.postReceipt || false,
  );
  const error = useSelector(
    (state: RootState) => state.commission.error.receiptList,
  );
  const saveError = useSelector(
    (state: RootState) => state.commission.error.postReceipt || "",
  );
  const selectedProviders = useSelector(
    (state: RootState) => state.commission.selectedProviders,
  );
  const loggedUser = useSelector((state: RootState) => state.authUser);
  const selectedDateValue = useSelector(
    (state: RootState) => state.commission.appliedSelectedDates,
  );
  const totalRows = useSelector(
    (state: RootState) => state.commission.totalRows,
  );
  const [editedActualValues, setEditedActualValues] = useState<{
    [key: string]: string;
  }>({});

  /* ================= TABLE PREFERENCE STATE ================= */
  const { preferences, status: preferencesStatus } = useSelector(
    (state: RootState) => state.preferences,
  );
  const pagination = useSelector(selectTablePagination(TABLE_NAME));
  const sorting = useSelector(selectTableSorting(TABLE_NAME));
  const columnOrder = useSelector(selectTableColumnOrder(TABLE_NAME));
  const columnVisibility = useSelector(selectTableColumnVisibility(TABLE_NAME));
  const preferenceAppliedFilters = useSelector(selectTableAppliedFilters(TABLE_NAME));
  const isDefaultPreference = useSelector(selectIsDefaultPreference(TABLE_NAME));
  const savedConfig = useMemo(
    () => preferences.tables.contextSpecific[TABLE_NAME],
    [preferences.tables.contextSpecific],
  );
  const tableConfig =
    preferences.tables.contextSpecific[TABLE_NAME] ??
    (defaultTableConfig.tables.contextSpecific as any)[TABLE_NAME];

  const hasTableChanges = useMemo(() => {
    if (!savedConfig) return false;
    return !isEqual(
      {
        filter: preferenceAppliedFilters,
        pagination,
        sorting,
        columnOrder,
        columnVisibility: columnVisibility ?? {},
      },
      {
        filter: savedConfig.filter,
        pagination: savedConfig.initialState?.pagination,
        sorting: savedConfig.initialState?.sorting,
        columnOrder: savedConfig.initialState?.columnOrder,
        columnVisibility: savedConfig.initialState?.columnVisibility ?? {},
      },
    );
  }, [preferenceAppliedFilters, pagination, sorting, columnOrder, columnVisibility, savedConfig]);

  /* ================= FILTER <-> PREFERENCE MIRROR ================= */
  // Commission's live filter UI (FilterTab + commissionSlice) stays the
  // source of truth for the current session. Date range filtering is
  // session-only and intentionally NOT part of the saved preference — only
  // the provider selection mirrors into preferenceSlice (for Save/Reset) and,
  // once on load, hydrates back out of the saved preference.
  const filterHydratedRef = useRef(false);
  const skipNextFilterMirrorRef = useRef(false);

  useEffect(() => {
    if (preferencesStatus !== "succeeded" || filterHydratedRef.current) return;
    filterHydratedRef.current = true;
    skipNextFilterMirrorRef.current = true; // this hydration's state change must not re-trigger applyTableFilters (which would reset pageIndex)
    dispatch(setSelectedProviders(preferenceAppliedFilters?.providers ?? []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferencesStatus]);

  useEffect(() => {
    if (!filterHydratedRef.current) return;
    if (skipNextFilterMirrorRef.current) {
      skipNextFilterMirrorRef.current = false;
      return;
    }
    dispatch(setTableDraftFilter({ tableName: TABLE_NAME, filter: { providers: selectedProviders } }));
    dispatch(applyTableFilters({ tableName: TABLE_NAME }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProviders]);

  const handlePaginationChange = (
    updater:
      | MRT_PaginationState
      | ((prev: MRT_PaginationState) => MRT_PaginationState),
  ) => {
    const next = typeof updater === "function" ? updater(pagination) : updater;
    dispatch(setTablePagination({ tableName: TABLE_NAME, pagination: next }));
  };

  const handleSortingChange = (
    updater: MRT_SortingState | ((prev: MRT_SortingState) => MRT_SortingState),
  ) => {
    const next = typeof updater === "function" ? updater(sorting) : updater;
    dispatch(setTableSorting({ tableName: TABLE_NAME, sorting: next }));
  };

  const handleColumnOrderChange = (
    updater: string[] | ((prev: string[]) => string[]),
  ) => {
    const next = typeof updater === "function" ? updater(columnOrder) : updater;
    dispatch(setTableColumnOrder({ tableName: TABLE_NAME, columnOrder: next }));
  };

  const handleColumnVisibilityChange = (
    updater: MRT_VisibilityState | ((prev: MRT_VisibilityState) => MRT_VisibilityState),
  ) => {
    const next = typeof updater === "function" ? updater(columnVisibility) : updater;
    dispatch(setTableColumnVisibility({ tableName: TABLE_NAME, columnVisibility: next }));
  };

  const handleSavePreference = async () => {
    try {
      await dispatch(
        saveTableState({ tableName: TABLE_NAME, shouldRefetch: true }),
      ).unwrap();
      showToast("User preferences have been updated successfully.", "", "success");
    } catch {
      showToast("Failed to save preferences", "", "error");
    }
  };

  const handleResetToDefault = async () => {
    try {
      await dispatch(resetTableStateToDefault({ tableName: TABLE_NAME })).unwrap();
      skipNextFilterMirrorRef.current = true; // the filter reset below must not immediately re-save via the mirror effect
      dispatch(setSelectedProviders([]));
      showToast("User preferences have been reset to default.", "", "success");
    } catch {
      showToast("Failed to reset preferences", "", "error");
    }
  };

  const providerOptions = useMemo(
    () =>
      providers.map((provider) => ({
        label: provider.label,
        checked: selectedProviders.some((p) => p.id === provider.id),
        id: provider.id,
      })),
    [providers, selectedProviders],
  );
  const { userId } = useSelector((state: RootState) => state.authUser);

  const initialDateValue = useMemo(() => {
    const today = moment().endOf("day");
    const start = today.clone().subtract(12, "months").startOf("month");
    const end = today;
    return `custom&start_date=${start.format("YYYY-MM-DD")}&end_date=${end.format("YYYY-MM-DD")}`;
  }, []);

  const fetchParams = useMemo(
    () => ({
      userId: userId,
      dateValue: selectedDateValue || initialDateValue,
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      providers: selectedProviders.map((p) => p.id).join(",") || "",
    }),
    [
      userId,
      selectedDateValue,
      initialDateValue,
      pagination.pageIndex,
      pagination.pageSize,
      selectedProviders,
    ],
  );

  const debouncedFetchReceiptList = useCallback(
    debounce((params) => {
      if (userId) {
        dispatch(fetchReceiptList(params));
      }
    }, 800),
    [dispatch, userId],
  );

  const fetchReceiptListImmediate = useCallback(
    (params: any) => {
      debouncedFetchReceiptList.cancel();
      if (userId) {
        dispatch(fetchReceiptList(params));
      }
    },
    [dispatch, debouncedFetchReceiptList],
  );

  useEffect(() => {
    if (loggedUser?.userId && providers.length === 0) {
      dispatch(fetchProviders({ page: 1, limit: 3000, fetchAll: true }));
    }
  }, [dispatch, loggedUser?.userId, providers.length]);

  useEffect(() => {
    if (preferencesStatus !== "succeeded") return;
    debouncedFetchReceiptList(fetchParams);
    return () => {
      debouncedFetchReceiptList.cancel();
    };
  }, [fetchParams, debouncedFetchReceiptList, preferencesStatus]);

  const CELL_RENDERER_MAP: Record<string, (props: any) => JSX.Element> = {
    ReactQuillComponent: ({ cell }) => (
      <ReactQuillComponent value={cell.getValue()} />
    ),
    highlightedDate: makeHighlightCell("date"),
    highlightedReceiptStatus: makeHighlightCell("receiptStatus"),
    highlightedCaseHolder: makeHighlightCell("caseHolder"),
    receiptActualInput: ({ row }: any) => {
      const expectationId = row.original.expectation_id;
      const value = editedActualValues[expectationId] ?? row.original.actual ?? "";

      return (
        <ActualInput
          value={value}
          onSave={(newValue: string) => {
            setEditedActualValues((prev) => ({
              ...prev,
              [expectationId]: newValue,
            }));
          }}
        />
      );
    },
  };

  const FILTER_FN_MAP: Record<string, (row: any, columnId: any, filterValue: any) => boolean> = {
    highlightedDate: makeHighlightFilterFn("date"),
    highlightedReceiptStatus: makeHighlightFilterFn("receiptStatus"),
    highlightedCaseHolder: makeHighlightFilterFn("caseHolder"),
  };

  const columns = useMemo<MRT_ColumnDef<any>[]>(() => {
    return (tableConfig.columns ?? []).map((col: any) => ({
      ...col,
      ...(col.Cell && typeof col.Cell === "string"
        ? { Cell: CELL_RENDERER_MAP[col.Cell] }
        : {}),
      ...(col.filterFn && typeof col.filterFn === "string"
        ? { filterFn: FILTER_FN_MAP[col.filterFn] }
        : {}),
    })) as MRT_ColumnDef<any>[];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableConfig, editedActualValues]);

  const sortingConfig = tableConfig.sortingConfig;
  const finalColumns = useMemo(
    () =>
      sortingConfig?.sortableColumns
        ? columns.map((col: any) => ({
            ...col,
            enableSorting: sortingConfig.sortableColumns.includes(col.accessorKey),
          }))
        : columns,
    [columns, sortingConfig],
  );

  // Update receiptedAmount for selected rows
  useEffect(() => {
    const total = selectedRowIds.reduce((sum, expectationId) => {
      const row = receiptList.find(
        (item) => item.expectation_id === expectationId,
      );
      const actualValue =
        editedActualValues[expectationId] ?? row?.actual ?? "0";
      return sum + (actualValue ? parseFloat(actualValue) : 0);
    }, 0);
    setReceiptedAmount(total);
  }, [selectedRowIds, receiptList, editedActualValues]);

  // Update unusedAmount
  useEffect(() => {
    const amount = parseFloat(amountReceived) || 0;
    const unused = amount - receiptedAmount;
    setUnusedAmount(isNaN(unused) ? 0 : unused);
  }, [amountReceived, receiptedAmount]);

  // Update activeRowItem and filteredData
  useEffect(() => {
    const timer = setTimeout(() => {
      if (selectedRowIds.length === 1) {
        const selectedRow = receiptList.find(item => item.expectation_id === selectedRowIds[0]);
        if (selectedRow) dispatch(setFilteredData(selectedRow));
        setActiveRowItem(selectedRow || {});
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [selectedRowIds, receiptList]);

  // Handle save for single or multiple selected rows
  const handleSave = async (data: {
    receipted_date: Date;
    description?: string;
  }) => {
    const rowsToUpdateAndPost = selectedRowIds
      .map((expectationId) => {
        const row = receiptList.find(
          (item) => item.expectation_id === expectationId,
        );
        if (!row) return null;

        const editedActual = editedActualValues[expectationId];
        const originalActual = row.actual;
        const hasActualChanged =
          editedActual !== undefined && editedActual !== originalActual;

        return {
          row,
          expectationId,
          actualAmount: editedActual ?? originalActual ?? "0.0000",
          shouldUpdate: hasActualChanged,
        };
      })
      .filter(Boolean) as {
        row: (typeof receiptList)[number];
        expectationId: string;
        actualAmount: any;
        shouldUpdate: boolean;
      }[];

    // 1. Update commissions where actual value is edited
    for (const { row, actualAmount, shouldUpdate } of rowsToUpdateAndPost) {
      if (shouldUpdate && row.commission_id && row.type) {
        const formData = {
          type: row.type,
          customer_id: row.customer_id,
          case_id: row.case_id,
          payment_id: row.payment_id,
          commission_id: row.commission_id,
          status: row.status,
          commission_type: row.commission_type,
          payment_source: row.payment_source,
          amount_to_company: parseFloat(actualAmount).toFixed(4),
          commission_frequency: row.commission_frequency,
          commission_startDate: row.commission_startDate,
          commission_endDate: row.commission_endDate,
          payment_count: row.payment_count,
          nb_register: row.nb_register,
          commission_note: row.commission_note,
          commission_module: true,
        };

        const result = await dispatch(
          updateCommission({ commissionId: row.commission_id, formData }),
        );
        if (updateCommission.rejected.match(result)) {
          showToast(String(result?.payload), "", "error");
          return;
        }
      }
    }

    // 2. Build receipt payload
    const payload = {
      receiptData: rowsToUpdateAndPost.map(
        ({ row, expectationId, actualAmount }) => ({
          commission_expectation_id: expectationId,
          expected_amount: Number(parseFloat(row.expected).toFixed(4)) || 0,
          due_date: row.due_date,
          commission_id: row.commission_id || expectationId,
          actual_amount: Number(parseFloat(actualAmount).toFixed(4)), // Convert to number
          receipted_date: moment(data.receipted_date).utc().toISOString(),
        }),
      ),
      unused: {
        unused: unusedAmount,
        receipted_description: data.description || "Commission receipted",
        receipted_date: moment(data.receipted_date).utc().toISOString(),
      },
    };

    // 3. Post receipt
    const result = await dispatch(postReceipt(payload));
    if (postReceipt.fulfilled.match(result)) {
      showToast(result.payload.message, "", "success");
      fetchReceiptListImmediate(fetchParams);
      setEditedActualValues({});
      setSelectedRowIds([]);
      setAmountReceived("");
      setConfirmsavepopup(false);
      return true; // Indicate success
    } else {
      showToast(`Failed to save: ${saveError || "Unknown error"}`, "", "error");
      return false; // Indicate failure
    }
  };

  const selectedMenuItem = useMemo(() => {
    const dateLabel =
      dateOptions.find((opt) => opt.value === dateOption)?.label ||
      "Last 12 Months";
    let formattedDateLabel = dateLabel;
    if (dateOption === "custom" && selectedDateValue) {
      formattedDateLabel = `Custom`;
    }
    return selectedProviders.length > 0 || selectedDateValue
      ? `${formattedDateLabel}, Providers (${selectedProviders.length})`
      : "Last 12 Months, Provider";
  }, [selectedDateValue, selectedProviders]);

  return (
    <>
      <Toaster toasterId={toasterId} position="top-end" timeout={5000} />

      <div className="commission_options">
        <div className="commission_filter">
          <Label>Amount Received</Label>
          <Input
            value={amountReceived}
            onChange={(e) => setAmountReceived(e.target.value)}
            type="number"
            placeholder="Enter amount"
          />
          {!convertStringToArray(
            process.env.REACT_APP_HIDE_CTA_BUTTON,
          ).includes("hide_cm_receipting_auto_apply") && (
              <ASCButton
                shape="rounded"
                className="asc-button-primary"
                appearance="primary"
                disabled={
                  !amountReceived ||
                    receiptList.length === 0 ||
                    !convertStringToArray(
                      process.env.REACT_APP_DISABLE_CTA_BUTTON,
                    ).includes("disable_cm_receipting_auto_apply")
                    ? false
                    : true
                }
                onClick={() => {
                  const currentPageRows = receiptList.slice(
                    pagination.pageIndex * pagination.pageSize,
                    (pagination.pageIndex + 1) * pagination.pageSize,
                  );
                  const selectedIds = currentPageRows.map(
                    (row: any) => row.expectation_id,
                  );
                  setSelectedRowIds(selectedIds);
                }}
                label="Auto Apply"
              />
            )}
          <p>Receipted</p>
          <Label>{receiptedAmount.toFixed(2)}</Label>
          <p>Unused</p>
          <Label>{unusedAmount.toFixed(2)}</Label>
        </div>
        <ASCButton
          shape="rounded"
          className="asc-button-primary"
          appearance="primary"
          disabled={!amountReceived || selectedRowIds.length === 0}
          onClick={() => setConfirmsavepopup(true)}
          label={saveLoading ? "Saving..." : "Save"}
        />
      </div>

      <div className="commission_module">
        {isLoading ? (
          <TableListLoader rows={10} columns={6} />
        ) : (
          <MaterialReactTable
            enableColumnResizing={true}
            renderTopToolbarCustomActions={() => (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  width: "100%",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                {!convertStringToArray(
                  process.env.REACT_APP_HIDE_CTA_BUTTON,
                ).includes("hide_cm_receipting_adjust_commission") && (
                    <ASCButton
                      shape="rounded"
                      className="asc-button-primary"
                      appearance="primary"
                      onClick={() => setConfirmationPopup(true)}
                      disabled={
                        Number(selectedRowIds?.length) !== 1 ||
                          convertStringToArray(
                            process.env.REACT_APP_DISABLE_CTA_BUTTON,
                          ).includes("disable_cm_receipting_adjust_commission")
                          ? true
                          : false
                      }
                      label="Adjust Commission"
                    />
                  )}
                <FilterTab
                  title="Filter"
                  providerOptions={providerOptions}
                  selectedMenuItem={selectedMenuItem}
                  getReceiptlistData={() =>
                    debouncedFetchReceiptList(fetchParams)
                  }
                  filterPopup={filterPopup}
                  setFilterPopup={setFilterPopup}
                  dateOption={dateOption}
                  setDateOption={setDateOption}
                />
                {!convertStringToArray(
                  process.env.REACT_APP_HIDE_CTA_BUTTON,
                ).includes("hide_cm_receipting_preference") && (
                    <>
                      <ASCButton
                        label="Save Preference"
                        shape="rounded"
                        appearance="secondary"
                        onClick={handleSavePreference}
                        disabled={!hasTableChanges}
                      />
                      <Tooltip content="Reset to Default" relationship="label" positioning="below">
                        <Button
                          icon={<ArrowClockwise20Regular />}
                          onClick={handleResetToDefault}
                          disabled={isDefaultPreference}
                        />
                      </Tooltip>
                    </>
                  )}
              </div>
            )}
            columns={finalColumns}
            rowCount={Number(totalRows) || 0}
            data={receiptList}
            enableColumnFilters={true}
            enableGlobalFilter={false}
            enableDensityToggle={false}
            enableRowSelection={true}
            enableSelectAll={true}
            enableColumnActions={false}
            enableColumnOrdering
            enableColumnDragging
            enableFullScreenToggle={true}
            enablePagination={true}
            onPaginationChange={handlePaginationChange}
            onSortingChange={handleSortingChange}
            onColumnOrderChange={handleColumnOrderChange}
            onColumnVisibilityChange={handleColumnVisibilityChange}
            state={{ pagination, sorting, columnOrder, columnVisibility }}
            manualPagination
            enableStickyHeader
            enablePinning
            initialState={{
              columnPinning: {
                left: ["mrt-row-select", "expected", "actual"],
              },
              density: "compact",
            }}
            muiTablePaginationProps={{
              rowsPerPageOptions: [10, 50, 100, 200, 500],
            }}
            displayColumnDefOptions={{
              "mrt-row-select": {
                header: "Received",
                enablePinning: false,
              },
            }}
            muiSelectAllCheckboxProps={({ table }) => {
              const currentPageRows = table
                .getRowModel()
                .rows.map((row) => row.original.expectation_id);

              const allSelected = currentPageRows.every((id) =>
                selectedRowIds.includes(id),
              );

              const someSelected =
                currentPageRows.some((id) => selectedRowIds.includes(id)) &&
                !allSelected;

              return {
                checked: allSelected,
                indeterminate: someSelected,
                onChange: () => {
                  setSelectedRowIds((prev) => {
                    if (allSelected) {
                      // UNSELECT current page rows
                      return prev.filter((id) => !currentPageRows.includes(id));
                    }

                    // SELECT current page rows
                    const merged = new Set([...prev, ...currentPageRows]);
                    return Array.from(merged);
                  });
                },
                color: "primary",
              };
            }}
            muiSelectCheckboxProps={({ row }: any) => ({
              checked: selectedRowIds.includes(row.original.expectation_id),
              onChange: () => {
                const expectationId = row.original.expectation_id;
                setSelectedRowIds((prev) =>
                  prev.includes(expectationId)
                    ? prev.filter((id) => id !== expectationId)
                    : [...prev, expectationId],
                );
              },
              color: "primary",
            })}
            positionActionsColumn="last"
            positionToolbarAlertBanner="none"
          // renderRowActions={({ row }) => (
          //   <Tooltip content="Edit" relationship="label" positioning="below">
          //     <ASCButton
          //       shape="rounded"
          //       appearance="subtle"
          //       onClick={() => {
          //         setSelectedRowIds([row.original.expectation_id]);
          //         setConfirmationPopup(true);
          //       }}
          //       icon={<Edit16Filled primaryFill="var(--button-color)" />}
          //     />
          //   </Tooltip>
          // )}
          />
        )}
      </div>

      <ConfirmationPopup
        setConfirmationPopup={setConfirmationPopup}
        confirmationPopup={confirmationPopup}
        editpopup={editpopup}
        setReceiptEditpopup={setReceiptEditpopup}
        setEditpopup={setEditpopup}

      />
      <RecieptForm
        setReceiptEditpopup={setReceiptEditpopup}
        setConfirmationPopup={setConfirmationPopup}
        setStopReceiptpopup={setStopReceiptpopup}
        receiptEditpopup={receiptEditpopup}
        getReceiptlistData={() => debouncedFetchReceiptList(fetchParams)}
        setSelectedRowIds={setSelectedRowIds}
      />

      <StopReceiptPopUp
        setStopReceiptpopup={setStopReceiptpopup}
        setReceiptEditpopup={setReceiptEditpopup}
        openModal={stopReceiptpopup}
        selectedItem={activeRowItem}
        notify={notify}
        getCommissionDataList={() => debouncedFetchReceiptList(fetchParams)}
      />
      <SavePopup
        confirmsavepopup={confirmsavepopup}
        receiptedAmount={receiptedAmount.toFixed(2)}
        targetedAmount={parseFloat(amountReceived).toFixed(2)}
        difference={unusedAmount.toFixed(2)}
        setConfirmsavepopup={setConfirmsavepopup}
        onSave={handleSave}
        saveError={saveError}
        getReceiptlistData={() => fetchReceiptListImmediate(fetchParams)}
      />
    </>
  );
};

export default ReceiptList;
